/**
 * Wellspring Meds global component tracking
 *
 * Supports:
 * - Explicit data-wsm-* attributes
 * - PDP Add to Cart and purchase options
 * - PLP product cards and quick add
 * - Algolia filters and pagination
 */

(() => {
  if (window.__wsmInteractionTrackingLoaded) return;

  window.__wsmInteractionTrackingLoaded = true;
  window.dataLayer = window.dataLayer || [];

  const cleanText = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);

  const setData = (element, key, value) => {
    if (!element || value === undefined || value === null) return;

    element.dataset[key] = String(value);
  };

  const getDestination = (element) => {
    const rawHref =
      element.getAttribute('href') ||
      element.closest('a')?.getAttribute('href') ||
      '';

    if (!rawHref || rawHref.startsWith('javascript:')) {
      return '';
    }

    try {
      const url = new URL(rawHref, window.location.origin);

      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return '';
    }
  };

  const getProductHandleFromUrl = (urlValue) => {
    if (!urlValue) return '';

    try {
      const url = new URL(urlValue, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);

      return match ? match[1] : '';
    } catch (error) {
      const match = String(urlValue).match(/\/products\/([^/?#]+)/);

      return match ? match[1] : '';
    }
  };

  const getProductHandleFromElement = (element) => {
    const productContainer = element.closest('[data-wsm-product]');

    if (productContainer?.dataset.wsmProduct) {
      return productContainer.dataset.wsmProduct;
    }

    const productLink =
      element.closest('a[href*="/products/"]') ||
      element
        .closest(
          '.grid--view-items-hover, .grid-view-item, .ais-Hits-item'
        )
        ?.querySelector('a[href*="/products/"]');

    return getProductHandleFromUrl(
      productLink?.getAttribute('href') || ''
    );
  };

  const getProductCardLabel = (card, fallbackElement) => {
    const titleElement = card?.querySelector(
      [
        '.grid-view-item__title',
        '.product-card__title',
        '.wsm-product-title',
        '.product-title',
        'h2',
        'h3'
      ].join(',')
    );

    return cleanText(
      titleElement?.textContent ||
        fallbackElement?.getAttribute('aria-label') ||
        fallbackElement?.getAttribute('title') ||
        fallbackElement?.textContent
    );
  };

  const markTracked = ({
    element,
    section,
    name,
    label,
    product,
    position
  }) => {
    if (!element) return;

    setData(element, 'wsmTrack', 'true');
    setData(element, 'wsmSection', section);
    setData(element, 'wsmElement', name);

    if (label) {
      setData(element, 'wsmLabel', cleanText(label));
    }

    if (product) {
      setData(element, 'wsmProduct', product);
    }

    if (position !== undefined && position !== '') {
      setData(element, 'wsmPosition', position);
    }
  };

  /**
   * PDP
   */
  const annotatePdp = () => {
    if (document.body.dataset.wsmPageGroup !== 'pdp') return;

    const productHandle =
      document.body.dataset.wsmPageName || '';

    document
      .querySelectorAll('[id^="ProductSection-"]')
      .forEach((section) => {
        setData(section, 'wsmSection', 'product_main');
        setData(section, 'wsmProduct', productHandle);
      });

    /*
     * Main and sticky Add to Cart buttons.
     */
    document
      .querySelectorAll(
        [
          '[id^="ProductSection-"] .wsm-pdp-buy-row .cs-btn-cart',
          '[id^="ProductSection-"] button[name="add"]',
          '[id^="ProductSection-"] [type="submit"][name="add"]',
          '.product-sticky-bar .cs-btn-cart',
          '.product-sticky-bar .cs-btn-cart-2'
        ].join(',')
      )
      .forEach((button) => {
        const isSticky = Boolean(
          button.closest('.product-sticky-bar')
        );

        markTracked({
          element: button,
          section: isSticky
            ? 'sticky_purchase_bar'
            : 'product_main',
          name: isSticky
            ? 'sticky_add_to_cart_click'
            : 'add_to_cart_click',
          label: isSticky
            ? 'Sticky add to cart'
            : 'Add to cart',
          product: productHandle
        });
      });

    /*
     * One-time purchase and Subscribe & Save options.
     */
    /*
     * One-time purchase and Subscribe & Save options.
     */
    document
      .querySelectorAll(
        '[id^="ProductSection-"] .wsm-purchase-option'
      )
      .forEach((option, optionIndex) => {
        const input = option.querySelector(
          'input[type="radio"], input[type="checkbox"]'
        );

        const detectionText = cleanText(
          [
            option.textContent,
            option.dataset.purchaseOption,
            option.dataset.option,
            input?.value,
            input?.name,
            input?.id,
            input?.className
          ]
            .filter(Boolean)
            .join(' ')
        ).toLowerCase();

        let optionLabel = `purchase_option_${optionIndex + 1}`;

        if (
          detectionText.includes('subscribe') ||
          detectionText.includes('subscription') ||
          detectionText.includes('selling_plan') ||
          detectionText.includes('selling-plan') ||
          detectionText.includes('delivery')
        ) {
          optionLabel = 'subscribe_and_save';
        } else if (
          detectionText.includes('buy one') ||
          detectionText.includes('one time') ||
          detectionText.includes('one-time') ||
          detectionText.includes('onetime') ||
          detectionText.includes('single purchase')
        ) {
          optionLabel = 'one_time_purchase';
        }

        markTracked({
          element: option,
          section: 'purchase_options',
          name: 'purchase_option',
          label: optionLabel,
          product: productHandle,
          position: optionIndex + 1
        });
      });
    /*
     * Subscription frequency selector.
     */
    document
      .querySelectorAll(
        '[id^="ProductSection-"] .wsm-purchase-frequency__select'
      )
      .forEach((select) => {
        markTracked({
          element: select,
          section: 'purchase_options',
          name: 'subscription_frequency',
          label:
            select.options?.[select.selectedIndex]?.text ||
            'Subscription frequency',
          product: productHandle
        });
      });
  };

  /**
   * PLP / Algolia product grid
   */
  const annotatePlp = () => {
    if (document.body.dataset.wsmPageGroup !== 'plp') return;

    const collectionRoot =
      document.querySelector('#Collection') || document;

    const cards = Array.from(
      collectionRoot.querySelectorAll(
        [
          '.grid--view-items-hover',
          '.grid-view-item',
          '.ais-Hits-item'
        ].join(',')
      )
    ).filter((card) =>
      card.querySelector('a[href*="/products/"]')
    );

    cards.forEach((card, cardIndex) => {
      const position = cardIndex + 1;
      const primaryProductLink = card.querySelector(
        'a[href*="/products/"]'
      );

      const productHandle = getProductHandleFromUrl(
        primaryProductLink?.getAttribute('href') || ''
      );

      const productLabel = getProductCardLabel(
        card,
        primaryProductLink
      );

      setData(card, 'wsmSection', 'product_grid');
      setData(card, 'wsmProduct', productHandle);
      setData(card, 'wsmPosition', position);

      /*
       * Image, title, or other product-page links.
       */
      card
        .querySelectorAll('a[href*="/products/"]')
        .forEach((link) => {
          if (
            link.matches(
              '.cs-add-to-cart, .cs-add-to-cart-cr'
            )
          ) {
            return;
          }

          const elementName = link.matches('.cs-learn-more')
            ? 'learn_more'
            : 'product_card';

          markTracked({
            element: link,
            section: 'product_grid',
            name: elementName,
            label: productLabel,
            product: productHandle,
            position
          });
        });

      /*
       * Quick Add / Add to Cart buttons.
       */
      card
        .querySelectorAll(
          [
            '.cs-add-to-cart',
            '.cs-add-to-cart-cr',
            'form[action*="/cart/add"] button[type="submit"]',
            'button[name="add"]'
          ].join(',')
        )
        .forEach((button) => {
          markTracked({
            element: button,
            section: 'product_grid',
            name: 'quick_add_to_cart',
            label: productLabel,
            product: productHandle,
            position
          });
        });
    });

    /*
     * Algolia and native collection filters.
     */
    collectionRoot
      .querySelectorAll(
        [
          '.ais-RefinementList-label',
          '.ais-Menu-link',
          '.ais-ToggleRefinement-label',
          '.ais-CurrentRefinements-delete',
          '.ais-ClearRefinements-button',
          '[data-filter-label]',
          '.filter-group label'
        ].join(',')
      )
      .forEach((filterElement) => {
        markTracked({
          element: filterElement,
          section: 'filters',
          name: 'filter_option',
          label:
            filterElement.getAttribute('aria-label') ||
            filterElement.textContent
        });
      });

    /*
     * Algolia and native pagination.
     */
    collectionRoot
      .querySelectorAll(
        [
          '.ais-Pagination-link',
          '.pagination a',
          '.pagination button'
        ].join(',')
      )
      .forEach((paginationElement) => {
        const label = cleanText(
          paginationElement.getAttribute('aria-label') ||
            paginationElement.textContent
        );

        const normalizedLabel = label.toLowerCase();

        let elementName = 'pagination_page';

        if (normalizedLabel.includes('next')) {
          elementName = 'pagination_next';
        } else if (
          normalizedLabel.includes('previous') ||
          normalizedLabel.includes('prev')
        ) {
          elementName = 'pagination_previous';
        }

        const numericPage = label.match(/\d+/)?.[0] || '';

        markTracked({
          element: paginationElement,
          section: 'pagination',
          name: elementName,
          label,
          position: numericPage
        });
      });
  };

  const annotateComponents = () => {
    annotatePdp();
    annotatePlp();
  };

  /*
   * Initial annotation.
   */
  annotateComponents();

  /*
   * Algolia refreshes the product grid after filtering,
   * sorting, searching, and pagination. Reapply the
   * attributes whenever the page DOM changes.
   */
  let annotationFrame = null;

  const observer = new MutationObserver(() => {
    if (annotationFrame) {
      window.cancelAnimationFrame(annotationFrame);
    }

    annotationFrame = window.requestAnimationFrame(() => {
      annotateComponents();
      annotationFrame = null;
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  /*
   * Global click handler.
   */
  document.addEventListener(
    'click',
    (event) => {
      if (
        window.Shopify &&
        window.Shopify.designMode
      ) {
        return;
      }

      const element = event.target.closest(
        '[data-wsm-track="true"]'
      );

      if (!element) return;

      const pageContainer =
        element.closest(
          '[data-wsm-page-group], [data-wsm-page-name]'
        ) || document.body;

      const sectionContainer = element.closest(
        '[data-wsm-section]'
      );

      const productContainer = element.closest(
        '[data-wsm-product]'
      );

      const positionContainer = element.closest(
        '[data-wsm-position]'
      );

      const slideContainer = element.closest(
        '[data-wsm-slide]'
      );

      const couponContainer = element.closest(
        '[data-wsm-coupon]'
      );

      const label = cleanText(
        element.dataset.wsmLabel ||
          element.getAttribute('aria-label') ||
          element.textContent
      );

      window.dataLayer.push({
        event: 'wsm_component_click',

        page_group:
          element.dataset.wsmPageGroup ||
          pageContainer?.dataset.wsmPageGroup ||
          '',

        page_name:
          element.dataset.wsmPageName ||
          pageContainer?.dataset.wsmPageName ||
          '',

        section_name:
          element.dataset.wsmSection ||
          sectionContainer?.dataset.wsmSection ||
          'unknown',

        element_name:
          element.dataset.wsmElement ||
          element.tagName.toLowerCase(),

        element_label: label,

        destination_path: getDestination(element),

        slide_index:
          element.dataset.wsmSlide ||
          slideContainer?.dataset.wsmSlide ||
          '',

        product_handle:
          element.dataset.wsmProduct ||
          productContainer?.dataset.wsmProduct ||
          getProductHandleFromElement(element),

        coupon_code:
          element.dataset.wsmCoupon ||
          couponContainer?.dataset.wsmCoupon ||
          '',

        element_position:
          element.dataset.wsmPosition ||
          positionContainer?.dataset.wsmPosition ||
          '',

        page_path: window.location.pathname
      });
    },
    true
  );
})();